import { Literal, Static, Union } from "runtypes";

export const EnvironmentType = Union(Literal("Cloud"), Literal("Classic"));

export type IEnvironmentType = Static<typeof EnvironmentType>;

export type IEnvironment = {
  name: string;
  display: string;
  topoName: string;
  clientId: string;
  baseUrl: string;
  toolTip: string;
  environmentType: IEnvironmentType;
  regionId: number | null;
};

/**
 * Given an environment variable name, retrieve it from process.env. If assertExists is true,
 * this method will throw an exception if it's unable to locate the variable.
 *
 * @param {string} name The environment variable to retrieve
 * @param {boolean} assertExists Whether or not to throw an exception if the variable can't be found
 * @returns {string} The value for the environment variable
 */
function retrieveEnvironmentVariable(name: string, assertExists: boolean): string {
  const environmentVariable = process.env[name];

  if (environmentVariable === undefined) {
    if (assertExists) {
      throw new Error(
        `Expected environment variable process.env.${name} to be defined, but was not found. This variable *must* be set in each stack-specific .env file.`,
      );
    } else {
      return "";
    }
  }

  return environmentVariable;
}

/**
 * Get information for the current primary environment of the TDS Website.
 *
 * @returns {IEnvironment} The current environment instance for TDS Web
 */
export function currentEnvironment(): IEnvironment {
  // e.g. "Stackb", "efdcprd"
  const env = retrieveEnvironmentVariable("REACT_APP_ENVIRONMENT", true);

  return environmentValues(env);
}

/**
 * Given an environment name, such as EFDCPRD or StackB, check the environment variables
 * to retrieve the provided value for the environment type, assert that it's a supported
 * environment type by checking against if it's either "Classic" or "Cloud", and then
 * returns the type.
 *
 * @param envName the environment name
 * @returns the environment type for the given environment
 */
function determineSuppliedEnvironmentType(envName: string): IEnvironmentType {
  const targetEnvironmentVariable = `REACT_APP_${envName}_ENVIRONMENT_TYPE`;

  const suppliedEnvironmentType = retrieveEnvironmentVariable(targetEnvironmentVariable, true);

  return EnvironmentType.check(suppliedEnvironmentType);
}

/**
 * Get environment values for a specific environment.
 *
 * @param envName the environment name
 * @returns The provided information for the specified environment
 */
export function environmentValues(envName: string): IEnvironment {
  const upperCaseEnvName = envName.toUpperCase();

  return {
    name: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_NAME`, true),
    display: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_DISPLAY`, true),
    topoName: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_TOPO_NAME`, true),
    clientId: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_CLIENT_ID`, true),
    baseUrl: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_BASE_URL`, true),
    toolTip: retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_TOOL_TIP`, false),
    environmentType: determineSuppliedEnvironmentType(upperCaseEnvName),
    regionId: parseInt(retrieveEnvironmentVariable(`REACT_APP_${upperCaseEnvName}_REGION_ID`, true)),
  };
}

export function environmentsToUse(): IEnvironment[] {
  // Add current environment
  const environments = [currentEnvironment()];

  // Retrieve others, if any
  const additionalEnv = process.env.REACT_APP_ADDTIONAL_ENVIRONMENTS;

  // Parse and add
  if (additionalEnv && additionalEnv !== "") {
    const elements = additionalEnv.split(",");

    elements.forEach((item) => {
      environments.push(environmentValues(item));
    });
  }

  return environments;
}
